import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, CheckSquare, ChevronDown, ChevronRight, ExternalLink, Lock, Pencil, Repeat, Route, Sparkles, Wrench } from 'lucide-react'
import { Button } from '../ui/Button'
import { Timestamp } from '../ui/Timestamp'
import { useToast } from '../ui/Toast'
import { useBranding } from '../../hooks/useBranding'
import { JourneyPreviewModal, loadDraftFromStorage, clearDraftFromStorage, formatDraftAge } from '../journey'
import { fromApiStep, stepFieldsFor, toApiSteps } from '../journey/stepKinds'
import type { BreakdownStep } from '../../types/journey'
import { GoalService, type Goal, type GoalBoardCard, type GoalBreakdownStep, type GoalJourneyPlan } from '../../services/goalService'
import { scheduleWords } from '../../utils/habitWords'
import { habitScheduleInput, levelWords } from '../../utils/skillWords'
import { dueLine, clockWithZone } from '../../utils/taskForm'

/**
 * A goal's journey (Goals UX pass 2026-09-06, graph progression 2026-09-07):
 * tasks, skills and habits, all three "alive and kicking".
 *
 * A step arrives when it becomes reachable rather than at accept — skills go in
 * straight away, since a skill has to exist for its level to gate anything, and
 * tasks and habits follow as the way clears. A step can wait on two different
 * things and the difference matters to the person: finite work that must be done
 * first, or a rung a skill has to reach. Both are named rather than described as
 * "waiting", and a step held up by a skill offers the way to that skill.
 *
 * The first ready, not-done task step is "start here". Then "Also building it":
 * whatever sits under the goal that is not a journey step. "Plan the journey"
 * asks the persona once and opens the review; "Write my own steps" opens it
 * empty; "Edit journey" for what is not done yet.
 */
const DRAFT_KEY = 'goal_journey_draft'
const KINDS = ['task', 'skill', 'habit'] as const

interface Props {
  goal: Goal
  card: GoalBoardCard | null
  live: boolean
  headingClass: string
  tz?: string | null
  onPlan: (plan: GoalJourneyPlan | null) => void
  refreshKey?: string | number | null
  autoStart?: boolean
  /** True when the page has no other next move: "Plan the journey" is then its one filled button (rule 3). */
  leads?: boolean
}

export function GoalJourney({ goal, card, live, headingClass, tz, onPlan, refreshKey, autoStart = false, leads = false }: Props) {
  const toast = useToast()
  const { assistant: personaName } = useBranding()
  const svc = useRef(new GoalService()).current
  const goalId = Number(goal.id)
  const [plan, setPlan] = useState<GoalJourneyPlan | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<BreakdownStep[] | null>(null)
  const [edges, setEdges] = useState<unknown[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)
  const [accepting, setAccepting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draftAge, setDraftAge] = useState<string | null>(null)
  const [resumed, setResumed] = useState(false)
  const autoStarted = useRef(false)

  const load = useCallback(async () => {
    try {
      const p = await svc.getJourneyPlan(goalId)
      setPlan(p); onPlan(p)
    } catch { setPlan(null); onPlan(null) }
    finally { setLoaded(true) }
  }, [svc, goalId, onPlan])
  useEffect(() => { void load() }, [load, refreshKey])
  useEffect(() => {
    const d = loadDraftFromStorage(DRAFT_KEY, goalId)
    setDraftAge(d ? formatDraftAge(d.savedAt) : null)
  }, [goalId, modalOpen])

  const openPreview = (steps: BreakdownStep[] | null, opts: { loading?: boolean; resumed?: boolean } = {}) => {
    setPreview(steps); setEditing(false); setCompletedCount(0); setResumed(Boolean(opts.resumed)); setModalLoading(Boolean(opts.loading)); setModalOpen(true)
  }

  const planJourney = async () => {
    setErr(null); setBusy(true)
    openPreview(null, { loading: true })
    try {
      const r = await svc.previewBreakdown(goalId)
      setEdges(Array.isArray(r.dependency_edges) ? r.dependency_edges : [])
      setPreview(r.steps.map((s) => fromApiStep(s, [...KINDS])))
    } catch (e) {
      setModalOpen(false)
      setErr(e instanceof Error ? e.message : 'Could not plan the journey just now')
    } finally { setBusy(false); setModalLoading(false) }
  }

  useEffect(() => {
    if (!autoStart || autoStarted.current || !loaded || plan) return
    autoStarted.current = true
    void planJourney()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loaded, plan])

  const resumeDraft = () => { const d = loadDraftFromStorage(DRAFT_KEY, goalId); if (d) openPreview(d.steps, { resumed: true }) }
  const discardDraft = () => { clearDraftFromStorage(DRAFT_KEY, goalId); setDraftAge(null); setResumed(false); setModalOpen(false); setPreview(null) }

  const accept = async (steps: BreakdownStep[]) => {
    setAccepting(true)
    try {
      const r = await svc.acceptBreakdown(goalId, toApiSteps(steps) as GoalBreakdownStep[], edges)
      clearDraftFromStorage(DRAFT_KEY, goalId); setDraftAge(null); setEdges([])
      setModalOpen(false); setPreview(null)
      await load()
      const kinds = { task: 0, skill: 0, habit: 0 } as Record<string, number>
      for (const s of r.steps || []) kinds[String(s.child_type || 'task')] = (kinds[String(s.child_type || 'task')] || 0) + 1
      const made = [kinds.task ? `${kinds.task} task${kinds.task === 1 ? '' : 's'}` : '', kinds.skill ? `${kinds.skill} skill${kinds.skill === 1 ? '' : 's'}` : '', kinds.habit ? `${kinds.habit} habit${kinds.habit === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ')
      toast.toast({ variant: 'success', title: 'Journey started', description: made ? `${made} in the journey. The first task is marked "start here"; the rest arrives as the journey reaches it.` : 'The first step is on its way.' })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the journey')
    } finally { setAccepting(false) }
  }

  const edit = () => {
    if (!plan) return
    // The graph and the skill plan travel with the step. Dropping them here sent
    // a step back stripped of what it waits on, so saving an edit flattened the
    // journey into a queue.
    const refOf = new Map(plan.steps.map((s) => [s.step_id, s.step_ref || s.step_id]))
    setPreview(plan.steps.map((s) => ({
      step_id: s.step_id,
      step_ref: s.step_ref || undefined,
      title: s.title,
      description: s.description || '',
      estimated_duration_minutes: s.estimated_duration_minutes,
      order: s.order,
      child_type: (KINDS as readonly string[]).includes(String(s.child_type)) ? (s.child_type as BreakdownStep['child_type']) : 'task',
      skill_plan: s.skill_plan || undefined,
      prerequisite_step_refs: (s.prerequisite_step_ids || []).map((id) => refOf.get(id)).filter((r): r is string => Boolean(r)),
      skill_requirements: (s.skill_requirement_ids || []).map((r) => ({ skill_step_ref: String(refOf.get(r.skill_step_id) || r.skill_step_ref || ''), required_level: r.required_level, required_capability: r.required_capability })).filter((r) => r.skill_step_ref),
    })))
    setCompletedCount(plan.steps.filter((s) => s.status === 'completed' || s.entity_id != null).length)
    setEditing(true); setResumed(false); setModalLoading(false); setModalOpen(true)
  }

  const saveEdit = async (steps: BreakdownStep[]) => {
    setSaving(true)
    try {
      await svc.updateJourneyPlan(goalId, toApiSteps(steps) as GoalBreakdownStep[])
      setModalOpen(false); setPreview(null); setEditing(false)
      await load()
      toast.success('Journey updated')
    } catch (e) {
      toast.error('Could not save the journey', e instanceof Error ? e.message : 'Please try again.')
    } finally { setSaving(false) }
  }

  const steps = plan?.steps || []
  const kindOf = (s: GoalJourneyPlan['steps'][number]) => String(s.entity_type || s.child_type || 'task')
  const titleOf = new Map(steps.map((s) => [s.step_id, s.title]))
  const skillStepById = new Map(steps.filter((s) => kindOf(s) === 'skill').map((s) => [s.step_id, s]))

  /**
   * What a waiting step is waiting on, in the person's own terms. "Waits" on its
   * own tells them nothing they can act on, and the reason code is for us. Work
   * that has to happen first comes before a rung, because it is the nearer of the
   * two and the one they can do something about.
   */
  const waitsOn = (s: GoalJourneyPlan['steps'][number]) => {
    const pending = (s.prerequisite_step_ids || []).filter((id) => titleOf.has(id) && steps.find((x) => x.step_id === id)?.status !== 'completed')
    if (pending.length) {
      const names = pending.map((id) => titleOf.get(id)).filter(Boolean)
      return { text: `Starts after ${names.join(' and ')}`, skill: null }
    }
    const req = (s.skill_requirement_ids || [])[0]
    const skillStep = req ? skillStepById.get(req.skill_step_id) : undefined
    if (req && skillStep) {
      const rung = req.required_level ? levelWords({ current_level: null as never, target_level: req.required_level as never }) : null
      return { text: `Starts once ${skillStep.title} is ${rung ? rung.replace(/^Aiming for /, '') : 'further along'}`, skill: skillStep }
    }
    return { text: 'Starts once what it needs is in place', skill: null }
  }
  const done = steps.filter((s) => s.status === 'completed').length
  const startHereId = steps.find((s) => kindOf(s) === 'task' && s.status !== 'completed' && s.status !== 'locked' && s.readiness_status !== 'blocked')?.step_id || null
  const inJourney = { task: new Set<number>(), habit: new Set<number>(), skill: new Set<number>() }
  for (const s of steps) { const k = kindOf(s); if ((k === 'task' || k === 'habit' || k === 'skill') && s.entity_id != null) inJourney[k].add(s.entity_id) }
  const taskById = new Map((card?.items.tasks || []).map((t) => [t.id, t]))
  const habitById = new Map((card?.items.habits || []).map((h) => [h.id, h]))
  const skillById = new Map((card?.items.skills || []).map((s) => [s.id, s]))
  const otherTasks = (card?.items.tasks || []).filter((t) => !inJourney.task.has(t.id))
  const otherHabits = (card?.items.habits || []).filter((h) => !inJourney.habit.has(h.id))
  const otherSkills = (card?.items.skills || []).filter((s) => !inJourney.skill.has(s.id))
  const tracked = otherTasks.length + otherHabits.length + otherSkills.length

  const habitLine = (h: NonNullable<ReturnType<typeof habitById.get>>) => {
    const doneToday = h.card?.today?.outcome === 'done'
    const words = scheduleWords(habitScheduleInput(h))
    return (
      <>
        {doneToday && h.card?.today?.at ? <>Done today <Timestamp value={h.card.today.at} mode="time" /></> : words || 'No schedule yet · set one on the habit'}
        {h.live && (h.card?.streak || 0) >= 2 ? <span className="text-ok"> · {h.card!.streak} in a row</span> : null}
        {h.live && !doneToday && h.card?.next ? <> · next <Timestamp value={h.card.next.at || `${h.card.next.date}T12:00:00`} mode={h.card.next.at ? 'datetime' : 'date'} /></> : null}
        {!h.live ? ' · paused' : null}
      </>
    )
  }
  const skillLine = (s: NonNullable<ReturnType<typeof skillById.get>>) => {
    const lv = levelWords({ current_level: s.current_level as never, target_level: s.target_level as never })
    const c = s.card
    return (
      <>
        {lv || 'No level set yet'}
        {c?.path ? <> · journey {c.path.status === 'completed' ? 'complete' : `${c.path.done} of ${c.path.total}`}</> : null}
        {c?.next?.title ? <> · next: {c.next.title}</> : c ? <> · {c.words}</> : null}
      </>
    )
  }
  const taskLine = (t: NonNullable<ReturnType<typeof taskById.get>>) => (
    t.next?.date ? <>On the calendar <Timestamp value={`${t.next.date}T12:00:00`} mode="date" />{t.next.time ? ` at ${clockWithZone(t.next.time, tz)}` : ''}</> : (dueLine(t) || (t.status === 'in_progress' ? 'In progress' : 'Not planned yet'))
  )
  const KindIcon = ({ k, className }: { k: string; className: string }) => (k === 'habit' ? <Repeat className={className} /> : k === 'skill' ? <Wrench className={className} /> : <CheckSquare className={className} />)

  return (
    <section data-testid="goal-journey">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex items-center gap-1.5 text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
          <h3 className={headingClass}><Route className="h-3.5 w-3.5 text-brand" /> Journey</h3>
          {plan && <span className="text-app-micro text-muted" data-testid="goal-journey-count">{plan.status === 'completed' ? 'complete' : `${done} of ${steps.length}`}</span>}
          {tracked > 0 && <span className="text-app-micro text-muted" data-testid="goal-journey-tracked">· {tracked} more tracked</span>}
        </button>
        {plan && plan.status !== 'completed' && live && (
          <button type="button" onClick={edit} className="ml-auto inline-flex items-center gap-1 text-app-micro font-medium text-brand hover:underline" data-testid="goal-journey-edit"><Pencil className="h-3 w-3" /> Edit journey</button>
        )}
      </div>

      {open && plan && (
        <ol className="mt-2 space-y-1.5" data-testid="goal-journey-steps">
          {steps.map((step, idx) => {
            const k = kindOf(step)
            const isDone = step.status === 'completed'
            const blocked = step.readiness_status === 'blocked'
            const locked = step.status === 'locked' || step.status === 'materialization_failed'
            const active = !isDone && !locked
            // Only a real dependency gets an explanation. A step held back purely
            // by the active-step cap is not waiting on anything the person can
            // act on, so saying "starts after…" about it would be a fiction.
            const waiting = !isDone && blocked && step.readiness_reason !== 'capacity' ? waitsOn(step) : null
            const startHere = step.step_id === startHereId
            const eid = step.entity_id ?? null
            const task = k === 'task' && eid != null ? taskById.get(eid) : undefined
            const habit = k === 'habit' && eid != null ? habitById.get(eid) : undefined
            const skill = k === 'skill' && eid != null ? skillById.get(eid) : undefined
            const href = eid != null ? `/${k}s/${eid}` : null
            return (
              <li key={step.step_id} className={`flex items-start gap-2 rounded-md text-sm ${startHere ? 'bg-brand-subtle/60 -mx-2 px-2 py-1.5' : ''}`} data-testid="goal-journey-step" data-kind={k} data-start-here={startHere || undefined} data-blocked={blocked || undefined}>
                <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-app-micro font-bold ${isDone ? 'bg-ok text-on-brand' : startHere ? 'bg-brand text-on-brand ring-2 ring-brand/30' : active && !blocked ? (k === 'task' ? 'bg-brand/80 text-on-brand' : 'bg-ok/20 text-ok') : 'bg-surface-alt text-muted'}`}>
                  {isDone ? <CheckCircle2 className="h-3 w-3" /> : k !== 'task' ? <KindIcon k={k} className="h-3 w-3" /> : locked || blocked ? <Lock className="h-3 w-3" /> : idx + 1}
                </span>
                <span className="min-w-0">
                  <span className={isDone || (active && !blocked) ? 'text-primary' : 'text-muted'}>{step.title}</span>
                  <span className="ml-2 rounded bg-surface-alt px-1 py-0.5 text-app-nano font-medium uppercase tracking-wider text-muted" data-testid="goal-journey-kind">{k}</span>
                  {startHere && <span className="ml-2 text-app-micro font-medium text-brand" data-testid="goal-journey-start-here">start here</span>}
                  {href && !locked && <Link to={href} className="ml-2 inline-flex items-center gap-1 text-app-micro text-brand hover:underline" data-testid="goal-journey-open"><ExternalLink className="h-3 w-3" /> open</Link>}
                  {isDone && step.completed_at && <span className="ml-2 text-app-micro text-muted">done <Timestamp value={step.completed_at} mode="date" /></span>}
                  {waiting && !isDone && (
                    <span className="block text-app-micro text-secondary" data-testid="goal-journey-waits">
                      {waiting.text}
                      {waiting.skill?.entity_id != null && (
                        <>
                          {' · '}
                          <Link to={`/skills/${waiting.skill.entity_id}`} className="text-brand hover:underline" data-testid="goal-journey-plan-skill">plan that skill</Link>
                        </>
                      )}
                    </span>
                  )}
                  {!waiting && active && k === 'task' && task && <span className="block text-app-micro text-secondary">{taskLine(task)}</span>}
                  {!waiting && active && k === 'habit' && <span className="block text-app-micro text-secondary">{habit ? habitLine(habit) : 'Ongoing'}</span>}
                  {!waiting && active && k === 'skill' && <span className="block text-app-micro text-secondary">{skill ? skillLine(skill) : 'Its own page holds where you are and its journey'}</span>}
                  {!waiting && locked && <span className="block text-app-micro text-muted">{step.status === 'materialization_failed' ? 'Could not be set up yet' : 'Set up when the journey reaches it'}</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}

      {open && loaded && !plan && (
        <div className="mt-2 space-y-2" data-testid="goal-journey-empty">
          <p className="text-app-meta text-secondary">No journey yet. {live ? `${personaName} can plan one — tasks, skills and habits together — you review it, keep or change what each step is, then start. Or write the steps yourself.` : 'It can be planned once this is picked back up.'}</p>
          {live && draftAge && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/20 bg-brand-subtle p-2 text-app-meta">
              <span className="text-primary">You have an unsaved journey, saved {draftAge}.</span>
              <Button size="xs" onClick={resumeDraft} data-testid="goal-journey-resume">Resume it</Button>
              <button type="button" onClick={discardDraft} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Discard</button>
            </div>
          )}
          {live && (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs" variant={leads && !draftAge ? 'default' : 'outline'} className={leads && !draftAge ? '' : 'hover:bg-brand-subtle'} disabled={busy} onClick={() => void planJourney()} data-testid="goal-journey-plan"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Plan the journey</Button>
              <button type="button" onClick={() => { setEdges([]); openPreview([]) }} className="text-app-micro font-medium text-brand hover:underline" data-testid="goal-journey-own">Write my own steps</button>
            </div>
          )}
          {busy && <p className="text-app-micro text-muted">Working on it…</p>}
        </div>
      )}

      {open && tracked > 0 && (
        <div className="mt-3" data-testid="goal-journey-items">
          <p className="text-app-micro font-medium uppercase tracking-widest text-muted">{plan ? 'Also building it' : 'Building it'}</p>
          <ul className="mt-1.5 space-y-1.5">
            {otherSkills.map((s) => (
              <li key={`s${s.id}`} className="flex items-start gap-2 text-sm" data-testid="goal-item-skill">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-alt text-secondary" aria-hidden="true"><Wrench className="h-3 w-3" /></span>
                <span className="min-w-0">
                  <Link to={`/skills/${s.id}`} className="text-primary hover:text-brand hover:underline">{s.title}</Link>
                  <span className="ml-2 text-app-micro text-muted">skill</span>
                  <span className="block text-app-micro text-secondary">{skillLine(s)}</span>
                </span>
              </li>
            ))}
            {otherHabits.map((h) => {
              const doneToday = h.card?.today?.outcome === 'done'
              return (
                <li key={`h${h.id}`} className="flex items-start gap-2 text-sm" data-testid="goal-item-habit" data-live={h.live}>
                  <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${doneToday ? 'bg-ok text-on-brand' : 'bg-surface-alt text-secondary'}`} aria-hidden="true">{doneToday ? <CheckCircle2 className="h-3 w-3" /> : <Repeat className="h-3 w-3" />}</span>
                  <span className="min-w-0">
                    <Link to={`/habits/${h.id}`} className={`hover:text-brand hover:underline ${h.live ? 'text-primary' : 'text-muted'}`}>{h.title}</Link>
                    <span className="ml-2 text-app-micro text-muted">habit</span>
                    <span className="block text-app-micro text-secondary">{habitLine(h)}</span>
                  </span>
                </li>
              )
            })}
            {otherTasks.map((t) => (
              <li key={`t${t.id}`} className="flex items-start gap-2 text-sm" data-testid="goal-item-task">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-alt text-secondary" aria-hidden="true"><CheckSquare className="h-3 w-3" /></span>
                <span className="min-w-0">
                  <Link to={`/tasks/${t.id}`} className="text-primary hover:text-brand hover:underline">{t.title}</Link>
                  <span className="ml-2 text-app-micro text-muted">task{t.is_recurring ? ' · repeats' : ''}{t.readiness_status === 'blocked' ? ' · waits on a skill' : ''}</span>
                  <span className="block text-app-micro text-secondary">{taskLine(t)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-bad" role="alert">{err}</p>}

      <JourneyPreviewModal
        open={modalOpen}
        onOpenChange={(o) => { if (!o) { setModalOpen(false); setPreview(null); setEditing(false) } }}
        title={editing ? 'Edit the journey' : `The journey for ${goal.title}`}
        description={editing ? 'Change what is not done yet. Finished steps and items already set up stay where they are.' : 'Change the steps until they read like yours. Each step is a task (done once, by when you say), a skill (its own page and journey) or a habit (comes round on a schedule). Then start: the skills go in straight away, and the rest arrives as the journey reaches it rather than all at once.'}
        steps={preview}
        loading={modalLoading}
        onStepsChange={setPreview}
        onAccept={accept}
        onSaveEdit={saveEdit}
        isEditingActive={editing}
        completedCount={completedCount}
        accepting={accepting}
        savingEdit={saving}
        draftKey={DRAFT_KEY}
        entityId={goalId}
        entityTitle={goal.title}
        resumedFromDraft={resumed}
        onDiscardDraft={discardDraft}
        showStepTypeSelector
        stepTypes={[...KINDS]}
        renderStepExtra={stepFieldsFor}
      />
    </section>
  )
}

export default GoalJourney
